import { Component, ComponentChildren } from 'preact'
import Categories from './categories'
import { ParentCategoryInfo } from '../mega-menu/Types'
import UnansweredTopics, { UnansweredTopicsProps } from './unanswered'
import RecentlyActiveTopics, { RecentlyActiveTopicsProps } from './recentlyActiveTopics'
import Phrase from '../../libs/translationsUtils/phrase'
import axios from 'axios'
import { Context, GetServerSideProps } from '../../server-middleware/context'
import {
  getById,
  getCategories,
  LegacyCategoryType,
  prepCategories
} from '../../server-middleware/categoryRepository'
import { z } from 'zod'
import Unified, { UnifiedTopic } from '../../services/unified'
import { ContentType } from '../utils/contentType'
import { TopicInfo, TopicType } from '../topic-list-view/TopicListItemView.sync'
import { common } from '../common'
import { queryZodType } from '../topic-list-view/TopicListView.sync'

type TabsType = Array<{
  enabled: boolean
  collapsed: boolean
  phrase?: string | undefined
  phrase_suffix: 'categories' | 'unanswered' | 'recent_active'
  class: string
}>

interface Props {
  tabs: TabsType
  parentCategories: ParentCategoryInfo[]
  collapsed: null | {
    [key: number]: boolean
  }
  displayUnreadPostCount: boolean
  unAnsweredTopics: UnansweredTopicsProps
  recentlyActiveTopics: RecentlyActiveTopicsProps
}

interface State {
  tabs: TabsType
  activeTab: number
  unreadTopicsAndRepliesCount: null | {
    [key: string]: {
      topicCount?: number
      postCount?: number
    }
  }
}

export const config = z.object({
  tabs: z.array(z.object({
    enabled: z.boolean(),
    collapsed: z.boolean(),
    phrase: z.union([z.string(), z.undefined()]),
    phrase_suffix: z.enum(['categories', 'unanswered', 'recent_active']),
    class: z.string()
  })),
  displayUnreadPostCount: z.boolean(),
  attachmentCdn: z.string(),
  recentlyActiveTopicsQuery: queryZodType,
  unansweredTopicsQuery: queryZodType
})

type Config = z.infer<typeof config>

function createUrl (unifiedTopic: UnifiedTopic, postId?: number): string {
  const url = `/fid-1/tid-${unifiedTopic.publicId}`
  if (postId) {
    return `${url}?postid=${postId}#post${postId}`
  }
  return url
}

function getType (categoryType: number|null, contentType: ContentType): TopicType {
  if (categoryType === LegacyCategoryType.ideation || contentType === 'idea') {
    return 'ideation'
  } else {
    return 'post'
  }
}

async function createTopicInfoFromUnifiedTopic (unifiedTopic: UnifiedTopic, unified: Unified, ctx: Context): Promise<TopicInfo> {
  const [firstPostAuthor, lastPostAuthor, category] = await Promise.all([
    unified.api.getUser(unifiedTopic.author.id),
    unified.api.getUser(unifiedTopic.lastContributor.id),
    getById(ctx, parseInt(unifiedTopic.categoryId))
  ])

  return {
    firstPost: {
      relativeCreationDate: common.relativeDate(new Date(unifiedTopic.createdAt).getTime()),
      url: createUrl(unifiedTopic),
      content: unifiedTopic.content,
      author: {
        id: firstPostAuthor.userid.toString(),
        userTitle: firstPostAuthor.usertitle
      }
    },
    relevantPost: {
      relativeCreationDate: common.relativeDate(new Date(unifiedTopic.createdAt).getTime()),
      url: createUrl(unifiedTopic),
      content: unifiedTopic.content,
      author: {
        id: firstPostAuthor.userid.toString(),
        userTitle: firstPostAuthor.usertitle
      }
    },
    lastPost: {
      relativeCreationDate: common.relativeDate(new Date(unifiedTopic.lastActivityAt).getTime()),
      url: createUrl(unifiedTopic, parseInt(unifiedTopic.lastPostId)),
      content: unifiedTopic.content,
      author: {
        id: lastPostAuthor.userid.toString(),
        userTitle: lastPostAuthor.usertitle
      }
    },
    forum: {
      url: `f-${unifiedTopic.categoryId}`,
      title: unifiedTopic.categoryName
    },
    featuredImage: unifiedTopic.featuredImage,
    contentType: unifiedTopic.contentType,
    content_type: unifiedTopic.contentType,
    type: getType(category ? category.type : null, unifiedTopic.contentType),
    isSticky: unifiedTopic.sticky,
    id: unifiedTopic.id,
    url: createUrl(unifiedTopic),
    title: unifiedTopic.title,
    numberOfViews: unifiedTopic.views,
    numberOfReplies: unifiedTopic.replyCount,
    numberOfUnreadReplies: 0, // TODO update this to be removed in SUC-704
    relativeCreationDate: common.relativeDate(new Date(unifiedTopic.createdAt).getTime()),
    isTopicUnread: true, // TODO update this to be removed in SUC-704
    hasCurrentUserLiked: ctx.userId ? unifiedTopic.likeSet.includes(ctx.userId) : false
  }
}

export const getServerSideProps: GetServerSideProps<Config, Props> = async (ctx, config) => {
  return await ctx.cache(`category-and-topic-stream_${JSON.stringify(config)}_${ctx.userId}`, async (): Promise<Props> => {
    const parentCategories = await prepCategories(
      ctx,
      await getCategories(ctx, [
        LegacyCategoryType.default,
        LegacyCategoryType.news,
        LegacyCategoryType.ideation
      ])
    )
    const unified = new Unified(ctx)
    const maxResults = 10

    const unifiedUnansweredQuestions = await Promise.all((await unified.findByQuery({
      page: 1,
      pageSize: maxResults,
      orderBy: 'lastActivityAt',
      contentType: 'question',
      hasBestAnswer: false
    })).data.result.topics.map(async unifiedUnansweredQuestion => await createTopicInfoFromUnifiedTopic(unifiedUnansweredQuestion, unified, ctx)))

    const unansweredQuestions: UnansweredTopicsProps = {
      tab: 'Help Others',
      requestUrl: '/widget/topics',
      query: config.unansweredTopicsQuery,
      attachmentCdn: config.attachmentCdn,
      topics: unifiedUnansweredQuestions
    }

    const unifiedRecentlyActiveTopics = await Promise.all((await unified.findByQuery({
      page: 1,
      pageSize: maxResults,
      orderBy: 'lastActivityAt'
    })).data.result.topics.map(async unifiedRecentlyActiveTopic => await createTopicInfoFromUnifiedTopic(unifiedRecentlyActiveTopic, unified, ctx)))

    const recentlyActiveTopics: RecentlyActiveTopicsProps = {
      tab: 'Recently Active',
      requestUrl: '/search/recentTopics',
      query: config.recentlyActiveTopicsQuery,
      attachmentCdn: config.attachmentCdn,
      topics: unifiedRecentlyActiveTopics
    }

    return {
      tabs: config.tabs,
      parentCategories: parentCategories,
      collapsed: null,
      displayUnreadPostCount: config.displayUnreadPostCount,
      unAnsweredTopics: unansweredQuestions,
      recentlyActiveTopics: recentlyActiveTopics
    }
  })
}

class CategoryAndTopicsStream extends Component<Props, State> {
  private readonly STORAGE_POSTFIX = 'tabs-helper-homepage'
  private readonly URLS = {
    categories: '#',
    unanswered: '/search/activity/unanswered',
    recent_active: '/search/activity/recent'
  }

  constructor (props: Props) {
    super(props)
    const enabledTabs = props.tabs.filter(tab => tab.enabled)
    const setActiveTab = typeof window === 'undefined' ? 0 : parseInt(window.localStorage.getItem(this.STORAGE_POSTFIX) ?? '1') - 1
    this.state = {
      tabs: enabledTabs,
      activeTab: setActiveTab >= enabledTabs.length ? 0 : setActiveTab,
      unreadTopicsAndRepliesCount: null
    }
    this.handleChange = this.handleChange.bind(this)
  }

  handleChange (idx: number) {
    this.setState({ activeTab: idx }, () => {
      if (typeof window === 'undefined') return

      window.localStorage.setItem(this.STORAGE_POSTFIX, (this.state.activeTab + 1).toString())
    })
  }

  async componentDidMount () {
    const topicIds: string[] = this.props.parentCategories.reduce((newList: string[], parentCategory) => (
      newList.concat(parentCategory.subforums.map(category => category.id.toString()))
    ), [])

    if (this.props.displayUnreadPostCount) {
      try {
        const { data } = await axios.get('/topic/unreadPostDataInReadableForums', {
          params: {
            topicIds
          }
        })
        this.setState({ unreadTopicsAndRepliesCount: data })
      } catch (e) {
        this.setState({ unreadTopicsAndRepliesCount: null })
      }
    }
  }

  render (_: Props, { tabs, activeTab }: State) {
    const categoriesTabIdx = tabs.findIndex(tab => tab.phrase_suffix === 'categories')
    const COMPONENTS: {
      [key: string]: ComponentChildren
    } = {
      categories: <Categories
        parentCategories={this.props.parentCategories}
        collapsed={this.props.collapsed ?? {}}
        displayUnreadPostCount={this.props.displayUnreadPostCount}
        unreadTopicsAndRepliesCount={this.state.unreadTopicsAndRepliesCount}
        isDefaultCollapsed={tabs[categoriesTabIdx]?.collapsed || false}
                  />,
      unanswered: <UnansweredTopics {...this.props.unAnsweredTopics} />,
      recent_active: <RecentlyActiveTopics {...this.props.recentlyActiveTopics} />
    }
    return (
      <div className='sitewidth'>
        <div className='box pagebox box--unbox'>
          <div className='box__content'>
            <div className='module widget--tabs tabs qa-widget-tabs'>
              <nav className='sections__nav sections__nav--accordion-S'>
                <div className='tabs--nav__container'>
                  <div className='tabs--nav'>
                    <ul className='tabs--navigation qa-tabs-menu'>
                      {
                        tabs.map((tab, idx) => (
                          <li key={tab.phrase_suffix} className='tabs__item tabs__item--M qa-tab' onClick={() => this.handleChange(idx)}>
                            <a
                              href={this.URLS[tab.phrase_suffix]}
                              onClick={(e) => e.preventDefault()}
                              className={`qa-link-tab js-tab ${tab.class}${activeTab === idx ? ' is-active' : ''}`}
                            >{tab.phrase || <Phrase p={`tab.navigation.${(tab.phrase_suffix || '').replace(' ', '_')}`} />}</a>
                          </li>
                        ))
                      }
                    </ul>
                  </div>
                </div>
                <div className='tabs--navigation'>
                  {
                    tabs.map((tab, idx) => (
                      <div className='tabs qa-tabs' key={tab.phrase}>
                        <div className={`js-content js-content-1 qa-tab-isVisible js-content-${idx + 1}${activeTab !== idx ? ' is-hidden--loose' : ''}`}>{COMPONENTS[tab.phrase_suffix]}</div>
                      </div>
                    ))
                  }
                  <div className='tabs qa-tabs' />
                </div>
              </nav>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default CategoryAndTopicsStream
